import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Menu, X, Globe, Factory } from "lucide-react";

export default function PublicNav() {
  const { t, lang, toggle } = useI18n();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/how-it-works", label: t("nav.how") },
    { to: "/capabilities", label: t("nav.capabilities") },
    { to: "/industries", label: t("nav.industries") },
    { to: "/about", label: t("nav.about") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-40" data-testid="public-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-9 h-9 rounded-md bg-brand-navy flex items-center justify-center">
              <Factory className="w-5 h-5 text-brand-amber" />
            </div>
            <div className="leading-tight">
              <div className="text-lg font-bold text-brand-navy">{t("brand")}</div>
              <div className="text-[10px] text-slate-500 tracking-wide">MERQAV</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.replace("/", "") || "home"}-link`}
                className={`px-3 py-2 text-sm rounded-md transition-colors ${loc.pathname === l.to ? "text-brand-navy font-semibold" : "text-slate-600 hover:text-brand-navy"}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={toggle}
              className="p-2 rounded-md hover:bg-slate-100 text-slate-600 flex items-center gap-1 text-sm"
              data-testid="lang-toggle-btn"
            >
              <Globe className="w-4 h-4" />
              <span className="hidden sm:inline">{lang === "ar" ? "EN" : "AR"}</span>
            </button>
            {user ? (
              <Link to="/app" data-testid="goto-dashboard-btn">
                <Button className="btn-cta hidden sm:inline-flex">{t("nav.dashboard")}</Button>
              </Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:inline-flex" data-testid="login-link">
                  <Button variant="ghost" className="text-brand-navy">{t("nav.login")}</Button>
                </Link>
                <Link to="/register" data-testid="register-link">
                  <Button className="btn-cta">{t("cta.request_part")}</Button>
                </Link>
              </>
            )}
            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden pb-4 space-y-1 border-t pt-2">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}-link`}
                className="block px-3 py-2 text-sm rounded-md hover:bg-slate-50"
              >
                {l.label}
              </Link>
            ))}
            {!user && (
              <Link to="/login" onClick={() => setOpen(false)} className="block px-3 py-2 text-sm font-semibold">
                {t("nav.login")}
              </Link>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
